import { useEffect, useMemo, useState } from "react";
import PageShell from "@/components/feature/PageShell";
import CastCard from "@/components/feature/CastCard";
import SearchInput from "@/components/base/SearchInput";
import ChipGroup from "@/components/base/ChipGroup";
import SelectField from "@/components/base/SelectField";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { castList, castInitials, castRoleTypes } from "@/utils/catalog";

const PAGE_SIZE = 12;

const sortOptions = [
  { label: "默认顺序", value: "default" },
  { label: "姓名首字母", value: "initial" },
  { label: "角色类型", value: "role" },
];

export default function CastCategoryPage() {
  const [keyword, setKeyword] = useState("");
  const [initial, setInitial] = useState("全部");
  const [roleType, setRoleType] = useState("全部");
  const [sort, setSort] = useState("default");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const list = castList.filter((cast) => {
      const matchInitial = initial === "全部" || cast.initial === initial;
      const matchRole = roleType === "全部" || cast.roleType === roleType;
      const matchKeyword =
        !q ||
        cast.name.toLowerCase().includes(q) ||
        cast.role.toLowerCase().includes(q) ||
        cast.roleIntro.toLowerCase().includes(q);
      return matchInitial && matchRole && matchKeyword;
    });
    const sorted = [...list];
    if (sort === "initial") sorted.sort((a, b) => a.initial.localeCompare(b.initial));
    if (sort === "role") sorted.sort((a, b) => a.roleType.localeCompare(b.roleType));
    return sorted;
  }, [keyword, initial, roleType, sort]);

  useEffect(() => {
    setPage(1);
  }, [keyword, initial, roleType, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setKeyword("");
    setInitial("全部");
    setRoleType("全部");
    setSort("default");
  };

  return (
    <PageShell active="cast" crumbs={[{ label: "首页", to: "/" }, { label: "演员名单" }]}>
      <header className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
        <h1 className="font-heading text-2xl font-semibold text-foreground-900">年轻的母亲-演员名单</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
          收录本剧主要演员及对应角色资料，按姓名首字母与角色类型分类，方便你快速找到想了解的演员。
        </p>
      </header>

      <div className="mt-5 flex flex-col gap-4 rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <SearchInput value={keyword} onChange={setKeyword} placeholder="搜索演员姓名或角色…" className="sm:max-w-xs" />
          <SelectField label="排序" value={sort} onChange={setSort} options={sortOptions} />
        </div>
        <ChipGroup label="姓名首字母" options={castInitials} value={initial} onChange={setInitial} />
        <ChipGroup label="角色类型" options={castRoleTypes} value={roleType} onChange={setRoleType} />
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
        <span>
          共找到 <span className="text-primary-600">{filtered.length}</span> 位演员
        </span>
        {(keyword || initial !== "全部" || roleType !== "全部") && (
          <button type="button" onClick={resetFilters} className="transition hover:text-primary-600">
            <i className="ri-refresh-line mr-1"></i>清除筛选
          </button>
        )}
      </div>

      {pageItems.length > 0 ? (
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 xl:grid-cols-4">
          {pageItems.map((cast) => (
            <CastCard key={cast.id} cast={cast} />
          ))}
        </div>
      ) : (
        <div className="mt-4">
          <EmptyState
            title="暂无符合条件的演员"
            description="尝试调整首字母或角色类型筛选条件。"
            actionText="重置筛选"
            onAction={resetFilters}
          />
        </div>
      )}

      <div className="mt-8">
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </PageShell>
  );
}